import { type ReactNode, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Surface } from "@/shared/ui";
import {
  BulbIcon,
  CalendarIcon,
  CloseIcon,
  CodeIcon,
  type IconProps,
  ListIcon,
  WriteIcon,
} from "./icons";

interface Capability {
  id: "write" | "summarize" | "plan" | "code" | "brainstorm";
  Icon: (props: IconProps) => ReactNode;
}

const CAPABILITIES: Capability[] = [
  { id: "write", Icon: WriteIcon },
  { id: "summarize", Icon: ListIcon },
  { id: "plan", Icon: CalendarIcon },
  { id: "code", Icon: CodeIcon },
  { id: "brainstorm", Icon: BulbIcon },
];

/** Suggestion slots per category — `chat.welcome.chips.<id>.s1..s5`. */
const SUGGESTION_SLOTS = ["s1", "s2", "s3", "s4", "s5"] as const;

/**
 * The empty-state hero (DESIGN §4.3): serif display + subhead, the composer,
 * and capability chips. A chip opens a panel of starter prompts for that
 * category (static + localized for now; rotation can come later) — picking
 * one fills the composer, where the user tweaks and sends.
 */
export function WelcomeState({
  composer,
  onChipSelect,
}: {
  composer: ReactNode;
  onChipSelect: (prompt: string) => void;
}) {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState<Capability["id"] | null>(null);
  const [pickedSlot, setPickedSlot] = useState<string | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  const openCategory = CAPABILITIES.find((c) => c.id === openId) ?? null;

  function toggleCategory(id: Capability["id"]) {
    setPickedSlot(null);
    setOpenId((current) => (current === id ? null : id));
  }

  // The panel floats over the content below (no layout shift), so it dismisses
  // like a popover: Escape or any pointer-down outside the chips+panel block.
  useEffect(() => {
    if (!openId) return;
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpenId(null);
    }
    function onPointerDown(event: PointerEvent) {
      if (anchorRef.current && !anchorRef.current.contains(event.target as Node)) {
        setOpenId(null);
      }
    }
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [openId]);

  return (
    <div className="flex min-h-full flex-col items-center px-6 py-6">
      <div className="flex w-full flex-1 flex-col items-center justify-center">
      <div className="w-full max-w-[720px] text-center">
        <h1 className="mb-8 font-serif text-[clamp(26px,3.4vw,34px)] font-medium leading-[1.1] tracking-[-0.015em] text-ink">
          {t("chat.welcome.titleLead")} {t("chat.welcome.titleEmph")}
        </h1>

        <div className="text-left">{composer}</div>

        <div ref={anchorRef} className="relative">
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            {CAPABILITIES.map(({ id, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => toggleCategory(id)}
                aria-expanded={openId === id}
                className={`inline-flex items-center gap-2 rounded-pill px-4 py-2 font-sans text-[13.5px] font-medium outline-none transition-[background,transform,color] duration-150 ease-house hover:-translate-y-px focus-visible:bg-tint ${
                  openId === id
                    ? "bg-accent text-accent-ink"
                    : "bg-bubble text-ink-soft hover:bg-tint hover:text-ink"
                }`}
              >
                <Icon className={`size-[15px] ${openId === id ? "text-accent-ink" : "text-ink-muted"}`} />
                {t(`chat.welcome.chips.${id}.label`)}
              </button>
            ))}
          </div>

          {openCategory ? (
            <div className="absolute inset-x-0 top-full z-10 mt-3 flex justify-center">
              <Surface
                tone="panel-2"
                border="none"
                radius="lg"
                shadow="pill"
                className="w-full max-w-[640px] text-left"
                role="region"
                aria-label={t(`chat.welcome.chips.${openCategory.id}.label`)}
              >
            <div className="flex items-center gap-2 px-4 pb-1 pt-3">
              <openCategory.Icon className="size-[15px] text-ink-muted" />
              <span className="flex-1 font-sans text-[12.5px] font-medium uppercase tracking-[0.06em] text-ink-muted">
                {t(`chat.welcome.chips.${openCategory.id}.label`)}
              </span>
              <button
                type="button"
                onClick={() => setOpenId(null)}
                aria-label={t("common.close")}
                className="grid size-7 place-items-center rounded-sm text-ink-faint outline-none transition-colors duration-150 ease-house hover:text-ink focus-visible:text-ink"
              >
                <CloseIcon className="size-4" />
              </button>
                </div>
                <div className="flex flex-col px-1 pb-1">
                  {SUGGESTION_SLOTS.map((slot) => {
                    const text = t(`chat.welcome.chips.${openCategory.id}.${slot}`);
                    return (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => {
                          setPickedSlot(slot);
                          onChipSelect(text);
                        }}
                        className={`rounded-sm px-3 py-3 text-left font-sans text-[14.5px] outline-none transition-colors duration-150 ease-house focus-visible:bg-line-soft ${
                          pickedSlot === slot
                            ? "bg-line-soft text-ink"
                            : "text-ink-soft hover:bg-line-soft hover:text-ink"
                        }`}
                      >
                        {text}
                      </button>
                    );
                  })}
                </div>
              </Surface>
            </div>
          ) : null}
        </div>

      </div>
      </div>

      <p className="pb-1 pt-6 font-sans text-[12px] tracking-[0.04em] text-ink-faint">
        {t("chat.composer.disclaimer")}
      </p>
    </div>
  );
}
